import { decodeJWT } from "@/utils/jwt-decoder";

// Função para extrair token do cookie u_token
const extractTokenFromCookie = (): string | null => {
  try {
    const cookies = document.cookie.split(";");
    const uTokenCookie = cookies.find((cookie) =>
      cookie.trim().startsWith("u_token="),
    );

    if (uTokenCookie) {
      return uTokenCookie.split("=")[1];
    }

    return null;
  } catch (error) {
    console.error("Erro ao extrair token do cookie:", error);
    return null;
  }
};

// Função para extrair spaceId do cookie space_id
const extractSpaceIdFromCookie = (): string | null => {
  try {
    const cookies = document.cookie.split(";");
    const spaceIdCookie = cookies.find((cookie) =>
      cookie.trim().startsWith("space_id="),
    );

    if (spaceIdCookie) {
      return spaceIdCookie.split("=")[1];
    }

    return null;
  } catch (error) {
    console.error("Erro ao extrair space_id do cookie:", error);
    return null;
  }
};

// Determina se usuário é externo baseado na liga da woba
const isExternalUser = (companyId: number): boolean => {
  const LOGIN_ALLOWED_LEAGUES = [189]; // Liga da woba
  return !LOGIN_ALLOWED_LEAGUES.includes(companyId);
};

export interface AuthValidationResult {
  isAuthenticated: boolean;
  isExternal: boolean;
  payload: any;
  token: string | null;
  error?: string;
}

export interface SpaceAccessValidationResult {
  hasAccess: boolean;
  error?: string;
}

/**
 * Validates user authentication and external route access
 * @param isExternalRoute - Whether the route is external (allows non-internal users)
 * @returns AuthValidationResult with authentication status and user info
 */
export const validateAuthentication = (
  isExternalRoute: boolean = false,
): AuthValidationResult => {
  const token = extractTokenFromCookie();

  if (!token) {
    return {
      isAuthenticated: false,
      isExternal: false,
      payload: null,
      token: null,
      error: "Token não encontrado",
    };
  }

  const payload = decodeJWT(token);
  if (!payload) {
    return {
      isAuthenticated: false,
      isExternal: false,
      payload: null,
      token,
      error: "Token inválido",
    };
  }

  const isExternal = payload.companyId
    ? isExternalUser(payload.companyId)
    : false;

  // If user is external (companyId !== 189) but trying to access internal route, deny access
  if (isExternal && !isExternalRoute) {
    return {
      isAuthenticated: false,
      isExternal,
      payload,
      token,
      error:
        "Acesso negado. Usuários externos só podem acessar rotas /external/",
    };
  }

  return {
    isAuthenticated: true,
    isExternal,
    payload,
    token,
  };
};

/**
 * Validates if user has access to a specific space
 * @param userSpaces - Array of space IDs the user has access to
 * @param requiredSpaceId - Space ID that needs to be accessed
 * @param isInternalUser - Whether user is internal (companyId 189) and should have full access
 * @returns SpaceAccessValidationResult with access status
 */
export const validateSpaceAccess = (
  userSpaces: number[] = [],
  requiredSpaceId: number | string,
  isInternalUser: boolean = false,
): SpaceAccessValidationResult => {
  const spaceId =
    typeof requiredSpaceId === "string"
      ? parseInt(requiredSpaceId)
      : requiredSpaceId;

  if (isNaN(spaceId)) {
    return {
      hasAccess: false,
      error: "ID do espaço inválido",
    };
  }

  // Internal users (companyId === 189) have access to ALL spaces
  if (isInternalUser) {
    return { hasAccess: true };
  }

  // External users (companyId !== 189) must have spaceId in their spaces array
  const hasAccess = userSpaces.includes(spaceId);

  if (!hasAccess) {
    return {
      hasAccess: false,
      error: `Acesso negado. Você não tem permissão para acessar dados do espaço ${spaceId}`,
    };
  }

  return { hasAccess: true };
};

/**
 * Complete authorization validation for routes that require space access
 * @param isExternalRoute - Whether the route allows external users
 * @param requiredSpaceId - Space ID that needs to be accessed (optional, will use cookie if not provided)
 * @returns Combined validation result
 */
export const validateRouteAccess = (
  isExternalRoute: boolean = false,
  requiredSpaceId?: number | string,
) => {
  // First validate authentication
  const authResult = validateAuthentication(isExternalRoute);

  if (!authResult.isAuthenticated) {
    return {
      hasAccess: false,
      isExternal: authResult.isExternal,
      payload: authResult.payload,
      token: authResult.token,
      error: authResult.error,
    };
  }

  // Determine spaceId to validate - use provided spaceId or get from cookie
  let spaceIdToValidate = requiredSpaceId;
  if (spaceIdToValidate === undefined) {
    const cookieSpaceId = extractSpaceIdFromCookie();
    if (cookieSpaceId) {
      spaceIdToValidate = cookieSpaceId;
    }
  }

  // If spaceId validation is required, validate space access for all routes
  if (spaceIdToValidate !== undefined) {
    const userSpaces = authResult.payload?.spaces || [];
    const isInternalUser = !authResult.isExternal; // Internal users (companyId === 189) are non-external
    const spaceResult = validateSpaceAccess(
      userSpaces,
      spaceIdToValidate,
      isInternalUser,
    );

    if (!spaceResult.hasAccess) {
      return {
        hasAccess: false,
        isExternal: authResult.isExternal,
        payload: authResult.payload,
        token: authResult.token,
        error: spaceResult.error,
      };
    }
  }

  return {
    hasAccess: true,
    isExternal: authResult.isExternal,
    payload: authResult.payload,
    token: authResult.token,
  };
};

/**
 * Quick validation using space_id from cookie - useful for any route
 * @param isExternalRoute - Whether the route allows external users
 * @returns Combined validation result using cookie space_id
 */
export const validateRouteAccessFromCookie = (
  isExternalRoute: boolean = false,
) => {
  return validateRouteAccess(isExternalRoute); // Will automatically use cookie space_id
};
